import { defineStore } from 'pinia'
import type { NuxtAxiosInstance } from '@/types/axios'

import { useUserInfo } from '@/composition/useMember'
import { promiseHandler } from '@/utils/promiseHandler'
import {
  getFunkiToken,
  getFunkiThreads,
  createFunkiThread,
  getFunkiMessages,
  createFunkiMessageStream,
  createFeedback,
  funkiApiClient,
} from '@/service/funki'

import type {
  FunkiMessage,
  FunkiUIMessage,
  FunkiMessageRole,
  FunkiFeedbackCreationPayload,
  FunkiFeedbackVote,
  FunkiApiError,
  FunkiLocation,
  FunkiSSEEvent,
  QueryChip,
} from '@/types/funki'
import { FunkiSSEEventType } from '@/types/funki'
import { RegionIdMap } from '@/view-models/region'
import { isFoodWineCategory } from '@/constants/funki'
import { checkIfRegionHomePage, checkIfRegionCategoriesPage } from '@/utils/pageHelpers'
import { parseSSEChunk } from '@/utils/sseParser'

// API Response Types are now defined in service/funki.ts

// Error Classes
export class FunkiGrantTokenError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FunkiGrantTokenError'
  }
}

export const FUNKI_MESSAGE_PAGE_SIZE = 20
export const MIN_MESSAGES_FOR_FEEDBACK = 4
const RETRY_DELAYS = [500, 1500]
const MAX_RETRIES = RETRY_DELAYS.length

export const useFunkiStore = defineStore('funki', () => {
  const { $i18n } = useNuxtApp()
  const { userInfo } = useUserInfo()

  let nuxtAxiosInstance: NuxtAxiosInstance | null = null
  const token = ref<string | null>(null)

  const threadId = ref<string | null>(null)
  const messages = ref<FunkiUIMessage[]>([])
  const isLoading = ref<boolean>(false)
  const isShowLoadMore = ref<boolean>(false)
  const isSendMessageError = ref<boolean>(false)
  const lastUserMessage = ref<string | null>(null)
  const location = ref<FunkiLocation | null>(null)
  const isStreaming = ref<boolean>(false)
  const lockedChipId = ref<string | null>(null)
  const retryCount = ref<number>(0)

  // control the JWT token refresh
  // not related to user interface, so not using ref
  let isRefreshing = false
  let refreshSubscribers: ((token: string) => void)[] = []
  let currentAbortController: AbortController | null = null
  let isStreamManuallyAborted = false

  // 抽象的錯誤處理函數，處理 token 過期和重試邏輯
  const handleTokenExpiredError = async <T>(error: unknown, retryFn: () => Promise<T>): Promise<T> => {
    const apiError = error as FunkiApiError
    if (apiError?.isTokenExpired) {
      try {
        await _handleTokenRefresh()
        return await retryFn()
      } catch (retryError: unknown) {
        console.error('Retry failed after token refresh:', retryError)
        throw retryError
      }
    }
    throw error
  }

  const messagesPagination = reactive({
    nextPageToken: '',
    limit: FUNKI_MESSAGE_PAGE_SIZE,
  })

  const canShowFeedback = computed(() => {
    if (!messages.value.length) return false
    if (isStreaming.value) return false

    return messages.value.length >= MIN_MESSAGES_FOR_FEEDBACK && !messages.value[messages.value.length - 1].isMocked
  })

  // List of regions that don't show Funki
  const EXCLUDED_REGIONS = [RegionIdMap.HONG_KONG, RegionIdMap.CHIANGMAI]

  // Determine whether to show Funki entry
  // Only show on homepage and food & wine category pages
  // Exclude specific regions, other regions require logged-in users
  const shouldShowFunkiEntry = computed(() => {
    const { $store: store } = useNuxtApp()
    const route = useRoute()

    if (import.meta.server) {
      return false
    }

    // Get current region ID
    const currentRegionId = store.getters['region/region']?.id || store.getters['region/indexRegion']?.id

    if (EXCLUDED_REGIONS.includes(currentRegionId)) {
      return false
    }

    // Require logged-in user
    if (!userInfo.value) {
      return false
    }

    // Check current page type
    const routeName = route.name

    // Homepage: use route name to determine
    if (checkIfRegionHomePage(routeName)) {
      return true
    }

    // Food & wine category page: use route name to determine
    if (checkIfRegionCategoriesPage(routeName)) {
      const categoryId = parseInt(route.params.id as string)
      if (isFoodWineCategory(currentRegionId, categoryId)) {
        return true
      }
    }

    // Other pages don't show
    return false
  })

  const init = async (axios: NuxtAxiosInstance) => {
    nuxtAxiosInstance = axios

    try {
      await _updateToken()
      await _updateThreadId()
      await updateThreadMessages()
    } catch (error) {
      console.error('Funki init error', error)
      if (error instanceof FunkiGrantTokenError) {
        // TODO: disable Funki feature when grant token failed
      }
    }
  }

  const updateThreadMessages = async (loadMore = false) => {
    if (isLoading.value || !token.value || !threadId.value) return

    isLoading.value = true

    // 使用 error-first 風格處理 Promise
    const [getMessagesError, res] = await promiseHandler(
      getFunkiMessages(token.value, threadId.value, messagesPagination.limit, loadMore ? messagesPagination.nextPageToken : undefined)
    )

    if (getMessagesError) {
      console.error('Failed to get messages:', getMessagesError)

      // 嘗試處理 token 過期錯誤
      const [retryError] = await promiseHandler(
        handleTokenExpiredError(getMessagesError, async () => {
          const { limit, nextPageToken } = messagesPagination
          const res = await getFunkiMessages(token.value!, threadId.value!, limit, loadMore ? nextPageToken : undefined)
          if (!res) return
          const data = res.data ?? []
          isShowLoadMore.value = data.length === limit
          messagesPagination.nextPageToken = res.pagination.next_page_token

          updateMessages(data, loadMore)
          return res
        })
      )

      if (retryError) {
        console.error('Failed to get messages after token refresh:', retryError)
      }
    } else if (res) {
      const data = res.data ?? []
      // Update the load more status
      isShowLoadMore.value = data.length === messagesPagination.limit
      updateMessages(data, loadMore)

      messagesPagination.nextPageToken = res.pagination.next_page_token
    }

    isLoading.value = false
  }

  const updateMessages = (newMessages: FunkiMessage[], loadMore = false) => {
    // Convert the created_at to milliseconds, and map quick_replies to quickReplies for UI
    const resReversed: FunkiUIMessage[] = newMessages.reverse().map(({ created_at, quick_replies, ...restMsg }: FunkiMessage) => ({
      ...restMsg,
      created_at: created_at * 1000,
      quickReplies: quick_replies ?? [],
    }))

    if (loadMore) {
      messages.value = [...resReversed, ...messages.value]
    } else {
      messages.value = resReversed
    }
  }

  const syncMessages = (newMessages: FunkiUIMessage[]) => {
    // Strip isNew to prevent re-animation on receiving tabs via BroadcastChannel
    messages.value = newMessages.map(msg => ({ ...msg, isNew: false }))
  }

  const sendMessage = async (content: string) => {
    if (!token.value || !threadId.value || !content) return
    if (isStreaming.value) return

    lastUserMessage.value = content

    isSendMessageError.value = false
    _addMessage(_genMockMessage('user', content))
    await startStream({ content, threadId: threadId.value })
  }

  const resendMessage = async () => {
    if (!token.value || !threadId.value || !lastUserMessage.value) return
    isSendMessageError.value = false
    await startStream({ content: lastUserMessage.value, threadId: threadId.value })
  }

  const removeSearchCondition = async (chip: QueryChip): Promise<void> => {
    if (isStreaming.value) {
      throw new Error('STREAMING_LOCKED')
    }

    lockedChipId.value = chip.id
    const removeMsg = $i18n.t('funki_remove_chip_msg', { value: chip.displayText })
    await sendMessage(removeMsg)
  }

  const createNewThread = async () => {
    messages.value = []
    isLoading.value = true

    // 使用 error-first 風格處理 Promise
    const [threadError, newThreadId] = await promiseHandler(_getNewThreadId())

    if (threadError) {
      console.error('Failed to create new thread:', threadError)
    } else {
      threadId.value = newThreadId ?? null
    }

    isLoading.value = false
    await updateThreadMessages()
  }

  const setLocation = (lat: number, lng: number) => {
    // Only set location if coordinates are valid (finite and within geographic bounds)
    const isValidCoords = Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
    if (isValidCoords) {
      location.value = { lat, lon: lng }
    }
  }

  const abortCurrentStream = () => {
    if (currentAbortController) {
      isStreamManuallyAborted = true
      currentAbortController.abort()
      currentAbortController = null
    }
    isStreaming.value = false
  }

  const sendFeedback = async (vote: FunkiFeedbackVote, reason: string | null) => {
    if (!token.value || !threadId.value || !messages.value.length) return

    const payload: FunkiFeedbackCreationPayload = {
      message_id: messages.value[messages.value.length - 1].id,
      vote,
      ...(reason ? { reason } : {}),
    }

    const mockMessage = _feedbackMessages.value[vote]

    _addMessage(_genMockMessage('user', mockMessage.user))

    isLoading.value = true

    // 使用 error-first 風格處理 Promise
    const [feedbackError] = await promiseHandler(createFeedback(token.value, threadId.value, payload))

    if (feedbackError) {
      console.error('Failed to submit feedback:', feedbackError)

      // 嘗試處理 token 過期錯誤
      const [retryError] = await promiseHandler(handleTokenExpiredError(feedbackError, () => createFeedback(token.value!, threadId.value!, payload)))

      if (retryError) {
        console.error('Failed to submit feedback after token refresh:', retryError)
      }
    }

    _addMessage(_genMockMessage('assistant', mockMessage.assistant))
    isLoading.value = false
  }

  const startStream = async (params: { content: string; threadId: string }) => {
    if (!import.meta.client && process.env.VITEST !== 'true') return
    if (!token.value || !params.threadId || !params.content) return
    if (isStreaming.value) return

    const botMsgId = crypto.randomUUID()
    const botMsg: FunkiUIMessage = {
      id: botMsgId,
      role: 'assistant',
      content: '',
      quickReplies: [],
      recommendations: [],
      search: null,
      created_at: Date.now(),
      isStreaming: true,
      progressText: '',
      streamingBuffer: '',
    }

    _addMessage(botMsg)
    const reactiveMsg = messages.value.find(m => m.id === botMsgId)!

    isStreamManuallyAborted = false
    isStreaming.value = true
    isLoading.value = true

    const payload = _buildMessagePayload(params.content)
    let attempt = 0

    while (attempt <= MAX_RETRIES) {
      let progressCount = 0
      const abortController = new AbortController()
      currentAbortController = abortController

      if (attempt > 0) {
        resetStreamMessageState(botMsgId)
      }

      try {
        const openStream = async () => {
          return await createFunkiMessageStream(token.value!, params.threadId, payload, {
            signal: abortController.signal,
          })
        }

        let response: Awaited<ReturnType<typeof createFunkiMessageStream>>

        try {
          response = await openStream()
        } catch (error) {
          response = await handleTokenExpiredError(error, openStream)
        }

        if (!response.body) {
          throw new Error('Response body is null')
        }
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let shouldStop = false

        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const parsed = parseSSEChunk(buffer)
            buffer = parsed.rest

            for (const event of parsed.events) {
              const result = processStreamEvent(event, progressCount, reactiveMsg)
              progressCount = result.progressCount

              if (result.shouldStop) {
                shouldStop = true
                break
              }
            }

            if (shouldStop) {
              break
            }
          }
        } finally {
          reader.releaseLock()
        }

        if (shouldStop) {
          _resetStreamingFlags()
          return
        }
        break
      } catch (error: unknown) {
        const streamError = error as Error

        if (currentAbortController === abortController) {
          currentAbortController = null
        }

        if (isStreamManuallyAborted || streamError?.name === 'AbortError' || abortController.signal.aborted) {
          _resetStreamingFlags()
          return
        }

        attempt += 1

        if (attempt > MAX_RETRIES) {
          resetStreamMessageState(botMsgId, { isStreaming: false })

          isSendMessageError.value = true
          _resetStreamingFlags()
          return
        }

        retryCount.value = attempt
        resetStreamMessageState(botMsgId)
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt - 1]))
      }
    }

    _resetStreamingFlags()
    currentAbortController = null
  }

  // private values & methods
  const _feedbackMessages = computed(() => ({
    up: { user: $i18n.t('funki_feedback_positive_user'), assistant: $i18n.t('funki_response_positive') },
    down: { user: $i18n.t('funki_feedback_negative_user'), assistant: $i18n.t('funki_response_negative') },
  }))

  const _updateToken = async () => {
    if (!nuxtAxiosInstance) return

    // 使用 error-first 風格處理 Promise
    const [tokenError, response] = await promiseHandler(getFunkiToken(nuxtAxiosInstance))

    if (tokenError) {
      console.error('Failed to get Funki token:', tokenError)
      throw new FunkiGrantTokenError('Failed to get Funki token')
    }

    if (!response?.data?.access_token) {
      throw new FunkiGrantTokenError('Failed to get Funki token')
    }

    token.value = response.data.access_token
    // 同步更新 singleton client 的 token
    funkiApiClient.setToken(response.data.access_token)
  }

  const _updateThreadId = async () => {
    if (!token.value) return

    // 使用 error-first 風格處理 Promise
    const [threadsError, response] = await promiseHandler(getFunkiThreads(token.value))

    if (threadsError) {
      console.error('Failed to get threads:', threadsError)
    } else {
      threadId.value = response.data?.id
    }

    if (!threadId.value) {
      const [newThreadError, newThreadId] = await promiseHandler(_getNewThreadId())
      if (newThreadError) {
        console.error('Failed to create new thread:', newThreadError)
      } else {
        threadId.value = newThreadId ?? null
      }
    }
  }

  const _getNewThreadId = async () => {
    if (!token.value) return

    // 使用 error-first 風格處理 Promise
    const [createError, response] = await promiseHandler(createFunkiThread(token.value))

    if (createError) {
      console.error('Failed to create thread:', createError)
      throw createError
    }

    return response.data?.id
  }

  const _addMessage = (msg: FunkiUIMessage) => {
    messages.value.push(msg)
  }

  const resetStreamMessageState = (botMsgId: string, options?: { isStreaming?: boolean }) => {
    const msg = messages.value.find(message => message.id === botMsgId)
    if (!msg) return

    msg.content = ''
    msg.progressText = ''
    msg.streamingBuffer = ''
    msg.isStreaming = options?.isStreaming ?? true
    msg.quickReplies = []
    msg.recommendations = []
    msg.search = null
    msg.serverId = undefined
  }

  const processStreamEvent = (event: FunkiSSEEvent, progressCount: number, msg: FunkiUIMessage): { shouldStop: boolean; progressCount: number } => {
    switch (event.type) {
      case FunkiSSEEventType.PROGRESS: {
        msg.progressText = event.data.message ?? ''

        return {
          shouldStop: false,
          progressCount: progressCount + 1,
        }
      }
      case FunkiSSEEventType.DELTA: {
        msg.streamingBuffer = (msg.streamingBuffer ?? '') + event.data.content
        msg.progressText = ''

        return { shouldStop: false, progressCount }
      }
      case FunkiSSEEventType.COMPLETED: {
        msg.serverId = event.data.id
        msg.content = event.data.content
        msg.quickReplies = event.data.quick_replies ?? []
        msg.recommendations = event.data.recommendations ?? []
        msg.search = event.data.search ?? null
        msg.isStreaming = false
        msg.progressText = ''

        return { shouldStop: false, progressCount }
      }
      case FunkiSSEEventType.DONE: {
        isStreaming.value = false
        currentAbortController = null

        return { shouldStop: true, progressCount }
      }
      default: {
        throw new Error(event.data.message ?? 'Stream error')
      }
    }
  }

  const _resetStreamingFlags = () => {
    isStreaming.value = false
    isLoading.value = false
    retryCount.value = 0
    isStreamManuallyAborted = false
  }

  const _handleTokenRefresh = async () => {
    if (isRefreshing) {
      return new Promise<string>(resolve => {
        _subscribeTokenRefresh((newToken: string) => {
          resolve(newToken)
        })
      })
    }

    try {
      isRefreshing = true
      await _updateToken()
      _onRefreshed()
      return token.value!
    } catch (error) {
      console.error('Failed to refresh token:', error)
      throw error
    } finally {
      isRefreshing = false
    }
  }

  const _buildMessagePayload = (content: string) => {
    const payload: { content: string; lat?: number; lon?: number } = { content }

    if (location.value) {
      payload.lat = location.value.lat
      payload.lon = location.value.lon
    }

    return payload
  }

  const _subscribeTokenRefresh = (callback: (token: string) => void) => {
    refreshSubscribers.push(callback)
  }

  const _onRefreshed = () => {
    if (token.value) {
      refreshSubscribers.forEach(callback => callback(token.value ?? ''))
    }
    refreshSubscribers = []
  }

  const _genMockMessage = (role: FunkiMessageRole, content: string): FunkiUIMessage => {
    return {
      id: Math.random().toString(36).substring(2, 9),
      role,
      content,
      created_at: Date.now(),
      isMocked: true,
      quickReplies: [],
      recommendations: [],
      search: null,
    }
  }

  watch(isStreaming, newVal => {
    if (!newVal) {
      lockedChipId.value = null
    }
  })

  return {
    threadId, // expose future usage
    messages,
    isLoading,
    isShowLoadMore,
    isSendMessageError,
    messagesPagination,
    canShowFeedback,
    shouldShowFunkiEntry,
    location,
    isStreaming,
    lockedChipId,

    init,
    updateThreadMessages, // expose future usage
    syncMessages,
    sendMessage,
    startStream,
    resendMessage,
    removeSearchCondition,
    createNewThread,
    sendFeedback,
    setLocation,
    abortCurrentStream,
  }
})
